import { ReactNode, useRef, useState } from 'react';

import styled from 'styled-components';

import { PopoverButton, PopoverButtonProps } from '@components/popover-button';

type PopoverScreenProps = Pick<
  PopoverButtonProps,
  'contentStyle' | 'placement' | 'offset' | 'panelOverflow' | 'isShiftedIntoView'
> & {
  trigger: ReactNode;
  children: ReactNode;
};

export type UsePopoverScreenProps<T> = Omit<PopoverScreenProps, 'children'> & {
  screens: Screens<T>;
  initialScreen: keyof T;
};

export type Screens<T> = { [key in keyof T]: JSX.Element };

export const PopoverScreen = ({
  trigger,
  placement,
  panelOverflow = 'hidden',
  ...rest
}: PopoverScreenProps) => (
  <PopoverButton
    trigger={TriggerPlaceholder}
    triggerProps={{ children: trigger }}
    placement={placement}
    contentStyle={{ display: 'flex', minWidth: 'fit-content', padding: 0 }}
    {...rest}
  />
);

const TriggerPlaceholder = styled.div({});

export const usePopoverScreens = <T extends object>({
  initialScreen,
  screens,
  ...rest
}: UsePopoverScreenProps<T>) => {
  const [currentScreen, setCurrentScreen] = useState<keyof T>(initialScreen);
  const setCurrentScreenDefault = (nextScreen?: keyof T) =>
    setCurrentScreen(nextScreen ?? initialScreen);
  const closeRef = useRef<() => void>();
  const screen = screens[currentScreen];
  const popover = (
    <PopoverScreen {...rest}>
      {({ close }: { close(): void }) => {
        closeRef.current = close;
        return screen;
      }}
    </PopoverScreen>
  );

  const reset = () => {
    setCurrentScreenDefault();
    closeRef.current?.();
  };

  return {
    popover,
    setCurrentScreen: setCurrentScreenDefault,
    currentScreen,
    close: closeRef?.current,
    reset,
  };
};
